import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, DollarSign, ArrowUpRight, ArrowDownRight, Download, 
  Calendar, Search, Filter, CreditCard, CheckCircle, Clock, Package,
  Wallet, PiggyBank, BarChart3, ChevronDown, RefreshCw, Eye,
  AlertCircle, Shield, Zap, Award, Users, ShoppingBag
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { toast } from 'react-toastify';
import { useSeller } from '../../context/SellerContext';

const CountUp = ({ end, duration = 1500, prefix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeProgress * end));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [end, duration]);

  return <span>{prefix}{count.toLocaleString()}</span>;
};

const StatCard = ({ title, value, subtitle, icon: Icon, color, bgColor, trend }) => (
  <div className="stat-card-premium">
    <div className="stat-icon-premium" style={{ backgroundColor: bgColor }}>
      <Icon size={20} color={color} />
    </div>
    <div className="stat-content-premium">
      <p className="stat-title-premium">{title}</p>
      <h3 className="stat-value-premium">{value}</h3>
      <div className="stat-footer-premium">
        <span className="stat-subtitle-premium">{subtitle}</span>
        {trend && (
          <span className="stat-trend-premium" style={{ color: trend > 0 ? '#10b981' : '#ef4444' }}>
            {trend > 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  </div>
);


const Earnings = () => {
  const { orders, loading, refreshData } = useSeller();
  const [payouts, setPayouts] = useState([]);
  const [chartView, setChartView] = useState('monthly');
  const [searchTxn, setSearchTxn] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [selectedPeriod, setSelectedPeriod] = useState('year');

  const fetchEarningsData = () => {
    refreshData();
    const localPayouts = JSON.parse(localStorage.getItem('sellerTransactions') || '[]');
    setPayouts(localPayouts);
  };

  useEffect(() => {
    fetchEarningsData();
    window.addEventListener('storage', fetchEarningsData);
    return () => {
      window.removeEventListener('storage', fetchEarningsData);
    };
  }, []);

  const deliveredOrders = orders.filter(o => o.status === 'Delivered');
  const totalOrdersCompleted = deliveredOrders.length;
  const totalEarningsAllTime = deliveredOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
  
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  
  const pendingSettlement = deliveredOrders
    .filter(o => (now - new Date(o.createdAt).getTime()) < SEVEN_DAYS_MS)
    .reduce((sum, o) => sum + (o.totalAmount || 0), 0);
    
  const totalPayoutsSum = payouts.reduce((sum, p) => sum + Math.abs(p.amount || 0), 0);
  const availableBalance = Math.max(0, totalEarningsAllTime - pendingSettlement - totalPayoutsSum);

  // Monthly earnings breakdown for pie chart
  const monthlyEarnings = deliveredOrders.reduce((acc, order) => {
    const month = new Date(order.createdAt).toLocaleString('default', { month: 'short' });
    acc[month] = (acc[month] || 0) + (order.totalAmount || 0);
    return acc;
  }, {});

  const pieData = Object.keys(monthlyEarnings).length > 0 
    ? Object.entries(monthlyEarnings).map(([name, value], index) => ({
        name,
        value,
        color: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'][index % 6]
      })).slice(0, 6)
    : [{ name: 'No Earnings', value: 1, color: '#e2e8f0' }];

  const handleWithdraw = () => {
    if (availableBalance <= 0) {
      toast.error('No available balance to withdraw.');
      return;
    }

    const newPayout = {
      id: `TXN-W${Date.now().toString().slice(-6)}`,
      type: 'Payout',
      amount: -availableBalance,
      date: new Date().toISOString(),
      status: 'Completed'
    };

    const updatedPayouts = [newPayout, ...payouts];
    setPayouts(updatedPayouts);
    localStorage.setItem('sellerTransactions', JSON.stringify(updatedPayouts));
    
    toast.success(`Successfully withdrew ₹${availableBalance.toLocaleString()}!`);
  };

  const generateChartData = () => {
    const dataMap = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    deliveredOrders.forEach(order => {
        const d = new Date(order.createdAt);
        let key = '';
        if (chartView === 'monthly') {
            key = months[d.getMonth()];
        } else {
            const weekNum = Math.ceil(d.getDate() / 7);
            key = `Wk ${weekNum} ${months[d.getMonth()]}`;
        }
        
        if (!dataMap[key]) dataMap[key] = 0;
        dataMap[key] += order.totalAmount;
    });

    if (Object.keys(dataMap).length < 2) {
      return [
        { name: chartView === 'monthly' ? 'Jan' : 'Wk 1', amount: 0 },
        { name: chartView === 'monthly' ? 'Feb' : 'Wk 2', amount: totalEarningsAllTime * 0.3 },
        { name: chartView === 'monthly' ? 'Mar' : 'Wk 3', amount: totalEarningsAllTime * 0.7 },
        { name: chartView === 'monthly' ? 'Apr' : 'Wk 4', amount: totalEarningsAllTime }
      ];
    }

    return Object.entries(dataMap).map(([k, v]) => ({ name: k, amount: v }));
  };

  const chartData = generateChartData();

  const transactions = [
    ...deliveredOrders.map(o => ({
      id: `TXN-R${o._id.slice(-4).toUpperCase()}`,
      type: 'Order Revenue',
      orderId: o._id.slice(-8).toUpperCase(),
      amount: o.totalAmount,
      date: new Date(o.createdAt),
      status: 'Completed'
    })),
    ...payouts.map(p => ({
      id: p.id,
      type: 'Payout',
      orderId: '—',
      amount: p.amount,
      date: new Date(p.date),
      status: p.status
    }))
  ].sort((a, b) => b.date - a.date);

  const filteredTransactions = transactions.filter(t => {
    const matchSearch = t.id.toLowerCase().includes(searchTxn.toLowerCase()) || t.orderId.toLowerCase().includes(searchTxn.toLowerCase());
    const matchFilter = filterType === 'All' || t.type === filterType;
    return matchSearch && matchFilter;
  });

  // StatCard was moved outside to prevent re-renders

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading earnings data...</p>
      </div>
    );
  }

  return (
    <div className="earnings-page">
      {/* Header */}
      <div className="earnings-header">
        <div className="header-left">
          <h1 className="page-title">Earnings & Revenue</h1>
          <p className="page-subtitle">Track your sales, settlements, and withdrawals</p>
        </div>
        <div className="header-right">
          <button className="btn-refresh" onClick={fetchEarningsData}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button onClick={handleWithdraw} className={`btn-withdraw ${availableBalance > 0 ? 'active' : 'disabled'}`}>
            <CreditCard size={18} />
            Withdraw Balance
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-premium-grid">
        <StatCard 
          title="Available Balance" 
          value={<CountUp end={availableBalance} prefix="₹" />} 
          subtitle="Ready to withdraw" 
          icon={Wallet} 
          color="#10b981" 
          bgColor="#d1fae5"
          trend={12}
        />
        <StatCard 
          title="Total Earnings" 
          value={<CountUp end={totalEarningsAllTime} prefix="₹" />} 
          subtitle="All-time revenue" 
          icon={TrendingUp} 
          color="#3b82f6" 
          bgColor="#dbeafe"
          trend={8}
        />
        <StatCard 
          title="Pending Settlement" 
          value={<CountUp end={pendingSettlement} prefix="₹" />} 
          subtitle="Clears in 7 days" 
          icon={Clock} 
          color="#f59e0b" 
          bgColor="#fed7aa"
          trend={-3}
        />
        <StatCard 
          title="Completed Orders" 
          value={<CountUp end={totalOrdersCompleted} />} 
          subtitle="Successfully delivered" 
          icon={Package} 
          color="#8b5cf6" 
          bgColor="#e9d5ff"
          trend={15}
        />
      </div>

      {/* Charts Section */}
      <div className="charts-premium-section">
        <div className="chart-main-card">
          <div className="chart-header">
            <div>
              <h5 className="chart-title">Revenue Overview</h5>
              <p className="chart-subtitle">Monthly earnings performance</p>
            </div>
            <div className="chart-controls">
              <select className="period-select-mini" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>
              <div className="view-toggle-mini">
                <button 
                  className={`toggle-btn ${chartView === 'weekly' ? 'active' : ''}`}
                  onClick={() => setChartView('weekly')}
                >
                  Weekly
                </button>
                <button 
                  className={`toggle-btn ${chartView === 'monthly' ? 'active' : ''}`}
                  onClick={() => setChartView('monthly')}
                >
                  Monthly
                </button>
              </div>
            </div>
          </div>
          <div className="chart-container-premium">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={(value) => `₹${value}`} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                  formatter={(value) => [`₹${value}`, 'Revenue']}
                />
                <Area type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorAmount)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-pie-card">
          <div className="chart-header">
            <h5 className="chart-title">Monthly Breakdown</h5>
            <p className="chart-subtitle">Earnings by month</p>
          </div>
          <div className="pie-container">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`₹${value}`, 'Earnings']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="transactions-card">
        <div className="transactions-header">
          <div>
            <h5 className="chart-title">Transaction History</h5>
            <p className="chart-subtitle">All your financial activities</p>
          </div>
          <div className="transactions-filters">
            <div className="search-wrapper-mini">
              <Search size={16} className="search-icon-mini" />
              <input 
                type="text" 
                className="search-input-mini" 
                placeholder="Search by ID..." 
                value={searchTxn}
                onChange={(e) => setSearchTxn(e.target.value)}
              />
            </div>
            <select 
              className="filter-select-mini"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="All">All Transactions</option>
              <option value="Order Revenue">Revenue</option>
              <option value="Payout">Payouts</option>
            </select>
            <button className="btn-export-mini">
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        <div className="transactions-table-container">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Type</th>
                <th>Order ID</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-row">
                    <div className="empty-transactions">
                      <CreditCard size={48} />
                      <h6>No transactions found</h6>
                      <p>You don't have any matching transactions.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((trx, idx) => (
                  <tr key={idx}>
                    <td className="txn-id">{trx.id}</td>
                    <td>
                      <span className={`type-badge ${trx.type === 'Payout' ? 'payout' : 'revenue'}`}>
                        {trx.type === 'Payout' ? 'Payout' : 'Revenue'}
                      </span>
                    </td>
                    <td className="order-id-txn">{trx.orderId}</td>
                    <td className="date-txn">{trx.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td className={`amount-txn ${trx.amount > 0 ? 'positive' : 'negative'}`}>
                      {trx.amount > 0 ? '+' : ''}₹{Math.abs(trx.amount).toLocaleString()}
                    </td>
                    <td>
                      <span className="status-badge-completed">
                        <CheckCircle size={12} />
                        Completed
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`.earnings-page {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.earnings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 12px;
}

.btn-refresh:hover {
  border-color: #10b981;
  color: #10b981;
}

.btn-withdraw {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.btn-withdraw.active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-withdraw.active:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-withdraw.disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}

.stats-premium-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  margin-bottom: 28px;
}

.stat-card-premium {
  background: white;
  border-radius: 24px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  transition: all 0.3s ease;
}

.stat-card-premium:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
}

.stat-icon-premium {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
}

.stat-content-premium {
  flex: 1;
}

.stat-title-premium {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 4px;
  font-weight: 500;
}

.stat-value-premium {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.stat-footer-premium {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.stat-subtitle-premium {
  font-size: 11px;
  color: #94a3b8;
}

.stat-trend-premium {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
}

.charts-premium-section {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 28px;
}

.chart-main-card,
.chart-pie-card {
  background: white;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.chart-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}

.period-select-mini {
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 12px;
  background: white;
}

.view-toggle-mini {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 40px;
}

.toggle-btn {
  padding: 4px 16px;
  border: none;
  background: transparent;
  border-radius: 40px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toggle-btn.active {
  background: white;
  color: #10b981;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.chart-container-premium {
  height: 300px;
}

.pie-container {
  height: 280px;
}

.transactions-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.transactions-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e8ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.transactions-filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.search-wrapper-mini {
  position: relative;
}

.search-icon-mini {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-input-mini {
  padding: 8px 12px 8px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  width: 220px;
}

.filter-select-mini {
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  background: white;
}

.btn-export-mini {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-export-mini:hover {
  border-color: #10b981;
  color: #10b981;
}

.transactions-table-container {
  overflow-x: auto;
}

.transactions-table {
  width: 100%;
  border-collapse: collapse;
}

.transactions-table th {
  text-align: left;
  padding: 14px 20px;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  border-bottom: 1px solid #e8ecef;
}

.transactions-table td {
  padding: 14px 20px;
  border-bottom: 1px solid #e8ecef;
  vertical-align: middle;
}

.transactions-table tr:hover {
  background: #f8fafc;
}

.txn-id {
  font-weight: 600;
  color: #1e293b;
  font-size: 13px;
}

.type-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.type-badge.revenue {
  background: #d1fae5;
  color: #10b981;
}

.type-badge.payout {
  background: #fee2e2;
  color: #ef4444;
}

.order-id-txn {
  font-family: monospace;
  font-size: 12px;
  color: #64748b;
}

.date-txn {
  font-size: 12px;
  color: #64748b;
}

.amount-txn {
  font-weight: 600;
  font-size: 14px;
}

.amount-txn.positive {
  color: #10b981;
}

.amount-txn.negative {
  color: #ef4444;
}

.status-badge-completed {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #d1fae5;
  color: #10b981;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
}

.empty-row td {
  padding: 60px 20px;
}

.empty-transactions {
  text-align: center;
  color: #94a3b8;
}

.empty-transactions svg {
  margin-bottom: 16px;
  color: #cbd5e1;
}

.empty-transactions h6 {
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 4px;
}

.empty-transactions p {
  font-size: 13px;
  margin: 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@media (max-width: 1024px) {
  .charts-premium-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .earnings-page {
    padding: 16px;
  }

  .stats-premium-grid {
    grid-template-columns: 1fr;
  }

  .transactions-filters {
    width: 100%;
    flex-direction: column;
  }

  .search-input-mini {
    width: 100%;
  }
}`}</style>
    </div>
  );
};

export default Earnings;